import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Phone,
  MessageCircle,
  Mail,
  Linkedin,
  Calendar,
  Video,
  Settings,
  X,
} from 'lucide-react';
import type { ReadinessItem } from '../../services/readinessService';

interface ConnectionsWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingConnections: ReadinessItem[];
  channelName: string;
}

const ICON_MAP: Record<string, React.ElementType> = {
  phone: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  linkedin: Linkedin,
  calendar: Calendar,
  video: Video,
};

export default function ConnectionsWarningModal({
  isOpen,
  onClose,
  missingConnections,
  channelName,
}: ConnectionsWarningModalProps) {
  const navigate = useNavigate();

  if (!isOpen || missingConnections.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-[scaleIn_200ms_ease-out]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <X size={16} className="text-gray-400" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-amber-50 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">
              Connections Required
            </h3>
            <p className="text-xs text-gray-500">
              for {channelName} campaign assignment
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-5 leading-relaxed">
          The following connections must be active before assigning leads to a {channelName} campaign.
        </p>

        <div className="space-y-2 mb-6">
          {missingConnections.map(conn => {
            const Icon = ICON_MAP[conn.key] || Settings;
            return (
              <div
                key={conn.key}
                className="flex items-center justify-between py-2.5 px-4 bg-red-50/60 border border-red-100 rounded-xl"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
                    <Icon size={14} className="text-red-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">{conn.label}</span>
                </div>
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/settings#${conn.settingsHash}`);
                  }}
                  className="text-xs font-semibold text-[#1875FF] hover:text-[#1260cc] px-2.5 py-1 rounded-lg hover:bg-[#1875FF]/5 transition-colors whitespace-nowrap"
                >
                  Connect
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onClose();
              const firstMissing = missingConnections[0];
              if (firstMissing) {
                navigate(`/settings#${firstMissing.settingsHash}`);
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:shadow-[#1875FF]/20 transition-all"
          >
            <Settings size={15} />
            Go to Settings
          </button>
        </div>
      </div>
    </div>
  );
}
